import { useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Reply, Send } from "lucide-react";
import type { Comment } from "@/lib/types";
import { cn } from "@/lib/utils";

interface CommentThreadProps {
	comments: Comment[];
	onSubmit: (text: string, parentId: string | null) => void;
}

function buildTree(comments: Comment[]): (Comment & { replies: Comment[] })[] {
	const roots: (Comment & { replies: Comment[] })[] = [];
	const childMap = new Map<string, Comment[]>();

	for (const c of comments) {
		if (c.parentId) {
			const arr = childMap.get(c.parentId) || [];
			arr.push(c);
			childMap.set(c.parentId, arr);
		}
	}

	for (const c of comments) {
		if (!c.parentId) {
			roots.push({ ...c, replies: childMap.get(c.id) || [] });
		}
	}

	return roots;
}

function getTimeAgo(dateStr: string): string {
	const diff = Date.now() - new Date(dateStr).getTime();
	const mins = Math.floor(diff / 60000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	return `${Math.floor(hrs / 24)}d ago`;
}

function CommentItem({
	comment,
	replies,
	onReply,
}: {
	comment: Comment;
	replies: Comment[];
	onReply: (parentId: string) => void;
}) {
	return (
		<div className="space-y-3">
			<div className="flex gap-3">
				<Avatar className="h-8 w-8 shrink-0">
					<AvatarFallback className="text-[10px] font-semibold bg-muted">
						{comment.author.avatar}
					</AvatarFallback>
				</Avatar>
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2 mb-0.5">
						<span className="text-sm font-medium">{comment.author.name}</span>
						<span className="text-xs text-muted-foreground">
							{getTimeAgo(comment.createdAt)}
						</span>
					</div>
					<p className="text-sm text-muted-foreground leading-relaxed">
						{comment.text}
					</p>
					<button
						onClick={() => onReply(comment.id)}
						className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mt-1.5 transition-colors"
					>
						<Reply className="h-3 w-3" />
						Reply
					</button>
				</div>
			</div>

			{replies.length > 0 && (
				<div className="ml-11 space-y-3 border-l-2 border-muted pl-4">
					{replies.map((reply) => (
						<div key={reply.id} className="flex gap-3">
							<Avatar className="h-7 w-7 shrink-0">
								<AvatarFallback className="text-[10px] font-semibold bg-muted">
									{reply.author.avatar}
								</AvatarFallback>
							</Avatar>
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2 mb-0.5">
									<span className="text-sm font-medium">
										{reply.author.name}
									</span>
									<span className="text-xs text-muted-foreground">
										{getTimeAgo(reply.createdAt)}
									</span>
								</div>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{reply.text}
								</p>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

export function CommentThread({ comments, onSubmit }: CommentThreadProps) {
	const [text, setText] = useState("");
	const [replyTo, setReplyTo] = useState<string | null>(null);
	const tree = buildTree(comments);

	const handleSubmit = () => {
		if (!text.trim()) return;
		onSubmit(text.trim(), replyTo);
		setText("");
		setReplyTo(null);
	};

	const replyComment = replyTo
		? comments.find((c) => c.id === replyTo)
		: null;

	return (
		<div className="space-y-4">
			<h3 className="font-semibold text-sm flex items-center gap-2">
				<MessageSquare className="h-4 w-4" />
				Discussion ({comments.length})
			</h3>

			{tree.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4 text-center">
					No comments yet. Start the conversation.
				</p>
			) : (
				<div className="space-y-4">
					{tree.map((c) => (
						<CommentItem
							key={c.id}
							comment={c}
							replies={c.replies}
							onReply={setReplyTo}
						/>
					))}
				</div>
			)}

			<div className="space-y-2 pt-2 border-t">
				{replyTo && replyComment && (
					<div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-md">
						<Reply className="h-3 w-3" />
						Replying to {replyComment.author.name}
						<button
							onClick={() => setReplyTo(null)}
							className="ml-auto hover:text-foreground"
						>
							Cancel
						</button>
					</div>
				)}
				<div className="flex gap-2">
					<Textarea
						placeholder="Add a comment..."
						value={text}
						onChange={(e) => setText(e.target.value)}
						className="min-h-[60px] text-sm resize-none"
						onKeyDown={(e) => {
							if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit();
						}}
					/>
					<Button
						size="sm"
						onClick={handleSubmit}
						disabled={!text.trim()}
						className="self-end"
					>
						<Send className="h-3.5 w-3.5" />
					</Button>
				</div>
			</div>
		</div>
	);
}
